import message from "@/utils/globalMessage";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Space, Tag, Tooltip, Badge } from "antd";
import {
  EyeOutlined, DeleteOutlined,
  ReloadOutlined, CloseOutlined, FilterOutlined,
} from "@ant-design/icons";
import Popconfirm from "@components/Feedback/Popconfirm";
import SearchLookup from "@components/SearchLookup";
import TableCreateIcon from "@components/Table/TableCreateIcon";
import ResponsiveTable from "@components/ResponsiveTable";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useEventBus, EventName } from "@/hooks/useEventBus";
import moment from "moment";
import { formatPMScheduleRuleSummary } from "../pmScheduleUtils";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import "./PMPlan.css";

const PM_PLAN_CONFIRM_POPUP_PROPS = {
  getPopupContainer: () => document.body,
  overlayStyle: { zIndex: 1500 },
  placement: "left",
};

const PM_PLAN_MOBILE_EXCLUDE_KEYS = [
  "name",
  "is_active",
  "schedule",
  "next_schedule_date",
  "cost_budget",
];

const PMPlan = () => {
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const detailPath = (id) => buildFacilityPath(`pm-plan-detail/${id}`, facilityContext) || "/switch-view";
  const createPath = buildFacilityPath("create-pm-plan", facilityContext) || "/switch-view";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [isMobileFilterDrawerOpen, setIsMobileFilterDrawerOpen] = useState(false);
  const [ordering, setOrdering] = useState("-created_date");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteVisible, setBulkDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (facilityId) params.append("facility", facilityId);
      if (searchText) params.append("search", searchText);
      if (ordering) params.append("ordering", ordering);
      const res = await api.filterPMPlan(params.toString());
      if (res.status === 200) {
        setData(res.data.results || []);
        setTotal(res.data.count || 0);
      }
    } catch {
      message.error("Failed to load Preventive Maintenance");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, facilityId, searchText, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEventBus(EventName.REFRESH_PM_PLAN_LIST, fetchData);

  const handleSearchLookup = useCallback(async (keyword) => {
    try {
      const params = new URLSearchParams({ page: 1, page_size: 15 });
      if (facilityId) params.append("facility", facilityId);
      if (keyword) params.append("search", keyword);
      const res = await api.filterPMPlan(params.toString());
      if (res.status === 200) {
        return {
          data: res.data.results || [],
          success: true,
          total: res.data.count || 0,
        };
      }
    } catch {
      // lookup suggestions are optional; table fetch handles errors
    }
    return { data: [] };
  }, [facilityId]);

  const handleSearchSelect = useCallback((value) => {
    setSearchText(value || "");
    setPage(1);
  }, []);

  const handleResetMobileFilters = useCallback(() => {
    setSearchText("");
    setPage(1);
  }, []);

  const handleBulkDelete = async () => {
    const rows = selectedRows.length > 0
      ? selectedRows
      : data.filter((record) => selectedRowKeys.includes(record.id));
    if (!rows.length) return;

    try {
      setDeleting(true);
      await Promise.all(rows.map((row) => api.deletePMPlan(row.id)));
      message.success("Deleted successfully");
      setBulkDeleteVisible(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      fetchData();
    } catch {
      message.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectionChange = (keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows((prevRows) => {
      const rowMap = new Map();
      [...prevRows, ...rows].forEach((row) => {
        if (row?.id != null) rowMap.set(row.id, row);
      });
      return keys.map((key) => rowMap.get(key)).filter(Boolean);
    });
  };

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setBulkDeleteVisible(false);
  };

  const renderMobileHeader = (record) => {
    const equipmentNames = Array.isArray(record.equipment_names) ? record.equipment_names : [];
    const equipmentLabel = equipmentNames.length > 0
      ? `${equipmentNames.length} equipment(s)`
      : "0 equipment(s)";
    const nextScheduleDate = record.next_schedule_date || "-";
    const isOverdue = record.next_schedule_date && moment(record.next_schedule_date).isBefore(moment(), "day");

    return (
      <div className="pm-plan-mobile-card">
        <div className="pm-plan-mobile-card-main">
          <span
            className="pm-plan-mobile-card-title pm-plan-mobile-card-title-link"
            title={record.name || ""}
            onClick={(event) => {
              event.stopPropagation();
              navigate(detailPath(record.id));
            }}
          >
            {record.name || "-"}
          </span>
          <span className="pm-plan-mobile-card-equipment-count" title={equipmentNames.join(", ")}>
            {equipmentLabel}
          </span>
        </div>
        {record.cycle_type ? (
          <div className="pm-plan-mobile-card-schedule">
            <span className="pm-plan-mobile-card-meta-label">Schedule: </span>
            <span className="pm-plan-mobile-card-meta-value">{formatPMScheduleRuleSummary(record)}</span>
          </div>
        ) : null}
        <div className="pm-plan-mobile-card-summary">
          <span className="pm-plan-mobile-card-meta">
            <span className="pm-plan-mobile-card-meta-label">Status: </span>
            <span className="pm-plan-mobile-card-meta-value">{record.is_active ? "Active" : "Inactive"}</span>
          </span>
          <span className="pm-plan-mobile-card-meta">
            <span className="pm-plan-mobile-card-meta-label">Next schedule date: </span>
            <span className={`pm-plan-mobile-card-meta-value ${isOverdue ? "pm-plan-mobile-card-date-overdue" : ""}`}>
              {nextScheduleDate}
            </span>
          </span>
          <span className="pm-plan-mobile-card-meta">
            <span className="pm-plan-mobile-card-meta-label">Budget: </span>
            <span className="pm-plan-mobile-card-meta-value">
              {record.cost_budget ? `$${parseFloat(record.cost_budget).toFixed(2)}` : "-"}
            </span>
          </span>
        </div>
      </div>
    );
  };

  const renderMobileRowActions = useCallback((record) => ([
    <Tooltip title="Detail" key="detail">
      <Button
        type="primary"
        size="small"
        icon={<EyeOutlined />}
        onClick={() => navigate(detailPath(record.id))}
        aria-label="Detail"
      >
        Details
      </Button>
    </Tooltip>,
  ]), [detailPath, navigate]);

  const renderBulkActionsBar = () => {
    if (!selectedRowKeys.length) return null;

    return (
      <div
        className="table-bulk-floating-bar"
        role="toolbar"
        aria-label="Bulk actions for selected rows"
      >
        <Space size={10} align="center" wrap className="table-bulk-floating-bar-inner">
          <span className="table-bulk-floating-bar-count">
            <span className="table-bulk-floating-bar-count-label">Selected</span>
            <span className="table-bulk-floating-bar-count-num">
              {selectedRowKeys.length}
            </span>
            <Tooltip title="Clear selection">
              <Button
                type="text"
                size="small"
                className="table-bulk-floating-bar-clear"
                icon={<CloseOutlined />}
                onClick={clearSelection}
                aria-label="Clear selection"
              />
            </Tooltip>
          </span>
          <Popconfirm
            title="Delete the selected PM plans?"
            description="This action cannot be undone!"
            open={bulkDeleteVisible}
            onConfirm={handleBulkDelete}
            onCancel={() => setBulkDeleteVisible(false)}
            {...PM_PLAN_CONFIRM_POPUP_PROPS}
          >
            <Tooltip title="Delete selected">
              <span>
                <Button
                  type="default"
                  danger
                  shape="circle"
                  className="table-bulk-floating-btn"
                  icon={<DeleteOutlined />}
                  loading={deleting}
                  onClick={() => setBulkDeleteVisible(true)}
                  aria-label="Delete selected"
                />
              </span>
            </Tooltip>
          </Popconfirm>
        </Space>
      </div>
    );
  };

  const renderPlanNameSearch = (style) => (
    <SearchLookup
      key={`pm-plan-name-search-${searchText || "empty"}`}
      name="name"
      placeholder="Plan name"
      defaultValue={searchText}
      style={style}
      SelectedLookup_CommonRequest={handleSearchSelect}
      Lookup_CommonupRequest={handleSearchLookup}
    />
  );

  const renderMobileFilterDrawer = () => (
    <Drawer
      placement="bottom"
      height="72vh"
      title="Filter and search"
      className="table-page-header-mobile-filter-drawer"
      visible={isMobileFilterDrawerOpen}
      onClose={() => setIsMobileFilterDrawerOpen(false)}
      destroyOnClose={false}
    >
      <div className="table-page-header-mobile-filter-content">
        <div className="mt-3 text-center table-page-header-filter-search-content">
          <div className="flex justify-between items-center border-b-2 py-2 table-page-header-filter-search-reset-row">
            <div></div>
            <button
              className="text-blue-600 font-bold uppercase px-6 py-2 rounded"
              onClick={handleResetMobileFilters}
            >
              Reset
            </button>
          </div>
          <div className="mt-2 px-7 py-3 table-page-header-filter-search-fields">
            <div className="mb-4 table-page-header-mobile-filter-field">
              <div className="table-page-header-filter-row">
                {renderPlanNameSearch({ width: "100%" })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );


  const columns = [
    {
      title: "Plan Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: ordering === "name" ? "ascend" : ordering === "-name" ? "descend" : null,
      render: (text, record) => (
        <div className="pm-plan-name-cell">
          <a
            className="pm-plan-name-link"
            title={text || ""}
            onClick={() => navigate(detailPath(record.id))}
          >
            {text || "-"}
          </a>
          <span className="pm-plan-equipment-count">
            {Array.isArray(record.equipment_names) ? record.equipment_names.length : 0} equipment(s)
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v) => (
        <Badge
          status={v ? "processing" : "default"}
          text={v ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => (
        <span>
          {record.cycle_type ? (
            <Tag color="cyan">{formatPMScheduleRuleSummary(record)}</Tag>
          ) : (
            "-"
          )}
        </span>
      ),
    },
    {
      title: "Next schedule date",
      key: "next_schedule_date",
      render: (_, record) => {
        const nextScheduleDate = record.next_schedule_date;
        if (!nextScheduleDate) return "-";
        const isOverdue = moment(nextScheduleDate).isBefore(moment(), "day");
        return <Tag color={isOverdue ? "red" : "blue"}>{nextScheduleDate}</Tag>;
      },
    },
    {
      title: "Budget",
      dataIndex: "cost_budget",
      key: "cost_budget",
      sorter: true,
      sortOrder: ordering === "cost_budget" ? "ascend" : ordering === "-cost_budget" ? "descend" : null,
      render: (v) => (v ? `$${parseFloat(v).toFixed(2)}` : "-"),
    },
  ];

  return (
    <div className="editable">
      <div className="table-page-header--root -mx-3 items-center w-full">
        <div className="table-page-header-toolbar-band max-w-full px-3 flex flex-nowrap items-center justify-start min-w-0 mt-2">
          <div
            className={[
              "flex table-page-header-main-row table-page-header-main-row--split filter-item justify-between w-full min-w-0 items-center",
              !isPC ? "table-page-header-main-row--mobile-filter-icon-only" : "",
            ].filter(Boolean).join(" ")}
          >
            <div className="table-page-header-filter-aux flex flex-nowrap items-center justify-start gap-3 min-w-0 flex-1">
              {isPC ? (
                <div className="table-page-header-filter-row">
                  {renderPlanNameSearch({ width: "20rem" })}
                </div>
              ) : null}
            </div>
            <div className="flex items-center table-page-header-actions">
              <Space size={8} align="center" wrap={false} className="table-page-header-icon-group">
                <Tooltip title="Refresh list">
                  <Button
                    type="default"
                    shape="circle"
                    className="table-page-header-icon-btn reload-refresh-btn"
                    icon={<ReloadOutlined />}
                    onClick={fetchData}
                    loading={loading}
                    aria-label="Refresh list"
                  />
                </Tooltip>
                <Tooltip title="New Preventive Maintenance">
                  <Button
                    type="default"
                    shape="circle"
                    className="table-page-header-icon-btn"
                    icon={<TableCreateIcon />}
                    onClick={() => navigate(createPath)}
                    aria-label="New Preventive Maintenance"
                  />
                </Tooltip>
                {!isPC ? (
                  <Tooltip title="Filter and search">
                    <Button
                      type="default"
                      shape="circle"
                      icon={<FilterOutlined className="table-page-header-filter-icon" />}
                      className={[
                        "table-page-header-icon-btn",
                        searchText ? "table-page-header-icon-btn--filtered" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => setIsMobileFilterDrawerOpen(true)}
                      aria-label="Filter and search"
                    />
                  </Tooltip>
                ) : null}
              </Space>
            </div>
          </div>
        </div>
      </div>
      <div className="px-3">
        <ResponsiveTable
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 900 }}
          rowSelection={{
            selectedRowKeys,
            onChange: handleSelectionChange,
            preserveSelectedRowKeys: true,
          }}
          onChange={(_, __, sorter, extra) => {
            if (extra?.action === "sort" && sorter?.field) {
              const dir = sorter.order === "ascend" ? "" : "-";
              setOrdering(`${dir}${sorter.field}`);
              setPage(1);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          hideMobileTableExpand={true}
          mobileCardHeaderRender={renderMobileHeader}
          mobileExpandedExcludeKeys={PM_PLAN_MOBILE_EXCLUDE_KEYS}
          mobileRowActions={renderMobileRowActions}
          hideMobileColumnActions={true}
        />
        {renderBulkActionsBar()}
      </div>
      {!isPC ? renderMobileFilterDrawer() : null}
    </div>
  );
};

export default PMPlan;
